import { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, ChevronRight, ChevronDown, Phone, Mail, Calendar, Users, DollarSign, Trash2, Edit2, MessageSquare, Clock, Search, AlertTriangle, Sparkles, User, CalendarPlus, Key, ExternalLink, StickyNote, Eye, EyeOff, Copy, Link, MapPin, Package, Save, ArrowUpDown, ArrowUp, ArrowDown, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import EmailComposeDialog from '@/components/EmailComposeDialog';
import CommunicationsLog from '@/components/CommunicationsLog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CONTACT_TYPES = [
  { value: 'contact', label: 'Contact', color: 'bg-gray-500' },
  { value: 'client', label: 'Client', color: 'bg-green-500' },
];

const LEAD_STATUSES = [
  { value: 'initial_contact', label: 'Initial Contact', color: 'bg-blue-500' },
  { value: 'follow_up', label: 'Follow Up', color: 'bg-yellow-500' },
  { value: 'proposal_sent', label: 'Proposal Sent', color: 'bg-purple-500' },
  { value: 'negotiating', label: 'Negotiating', color: 'bg-orange-500' },
  { value: 'booked', label: 'Booked', color: 'bg-green-500' },
  { value: 'lost', label: 'Lost', color: 'bg-red-500' },
];

export default function ContactsPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [contacts, setContacts] = useState([]);
  const [expandedContacts, setExpandedContacts] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc' for name sorting
  const [showEventRequestDialog, setShowEventRequestDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [venues, setVenues] = useState([]);
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [showAvailabilityWarning, setShowAvailabilityWarning] = useState(false);
  const [availabilityWarningData, setAvailabilityWarningData] = useState(null);
  
  // Venue search state
  const [venueSearchQuery, setVenueSearchQuery] = useState('');
  const [showVenueDropdown, setShowVenueDropdown] = useState(false);
  const [showAddVenueDialog, setShowAddVenueDialog] = useState(false);
  const [newVenueData, setNewVenueData] = useState({ name: '', address: '', city: '', venue_type: 'Other' });
  const venueSearchRef = useRef(null);
  
  // Password dialog state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordContact, setPasswordContact] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Email dialog state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState(null);
  const [communicationsRefresh, setCommunicationsRefresh] = useState(0);
  
  // Internal notes state
  const [newInternalNote, setNewInternalNote] = useState('');
  
  const [newEventRequest, setNewEventRequest] = useState({
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    event_date: '',
    start_time: '',
    end_time: '',
    event_type: '',
    venue: '',
    guest_count: '',
    budget: '',
    package_id: '',
    package: '',
    selected_addons: [],
    total_amount: '',
    deposit_amount: '',
    notes: ''
  });
  
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    contact_type: 'contact',
    notes: ''
  });

  useEffect(() => {
    fetchContacts();
    fetchEventTypes();
    fetchVenues();
    fetchPackages();
    fetchAddons();
    
    // Close venue dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (venueSearchRef.current && !venueSearchRef.current.contains(event.target)) {
        setShowVenueDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(`${API}/event-types`);
      setEventTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch event types:', error);
    }
  };

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API}/venues`);
      setVenues(response.data);
    } catch (error) {
      console.error('Failed to fetch venues:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const fetchAddons = async () => {
    try {
      const response = await axios.get(`${API}/addons`);
      setAddons(response.data);
    } catch (error) {
      console.error('Failed to fetch addons:', error);
    }
  };

  const checkDateAvailability = async (date) => {
    try {
      const response = await axios.post(`${API}/availability/check`, { date });
      return response.data;
    } catch (error) {
      console.error('Availability check failed:', error);
      return { available: true };
    }
  };

  // Package selection handler
  const handlePackageSelect = (packageId) => {
    const selectedPkg = packages.find(p => p.id === packageId);
    if (selectedPkg) {
      setNewEventRequest(prev => ({
        ...prev,
        package_id: packageId,
        package: selectedPkg.name,
        total_amount: selectedPkg.base_price,
        selected_addons: []
      }));
    }
  };

  // Addon toggle handler
  const handleAddonToggle = (addonId) => {
    const selected = newEventRequest.selected_addons.includes(addonId)
      ? newEventRequest.selected_addons.filter(id => id !== addonId)
      : [...newEventRequest.selected_addons, addonId];

    const selectedPkg = packages.find(p => p.id === newEventRequest.package_id);
    const basePrice = selectedPkg ? selectedPkg.base_price : 0;
    const addonsTotal = selected.reduce((sum, id) => {
      const addon = addons.find(a => a.id === id);
      return sum + (addon ? addon.price : 0);
    }, 0);

    setNewEventRequest({
      ...newEventRequest,
      selected_addons: selected,
      total_amount: basePrice + addonsTotal
    });
  };

  // Venue search handlers
  const handleVenueSearchChange = (e) => {
    const value = e.target.value;
    setVenueSearchQuery(value);
    setNewEventRequest(prev => ({ ...prev, venue: value }));
    setShowVenueDropdown(value.length > 0);
  };

  const selectVenue = (venue) => {
    setNewEventRequest(prev => ({
      ...prev,
      venue: venue.name
    }));
    setVenueSearchQuery(venue.name);
    setShowVenueDropdown(false);
  };

  const openAddVenueDialog = () => {
    setNewVenueData({
      name: venueSearchQuery,
      address: '',
      city: '',
      venue_type: 'Other'
    });
    setShowAddVenueDialog(true);
    setShowVenueDropdown(false);
  };

  const saveNewVenue = async () => {
    if (!newVenueData.name.trim()) {
      toast.error('Venue name is required');
      return;
    }
    
    try {
      await axios.post(`${API}/venues`, newVenueData);
      toast.success('New venue added!');
      
      setNewEventRequest(prev => ({
        ...prev,
        venue: newVenueData.name
      }));
      setVenueSearchQuery(newVenueData.name);
      setShowAddVenueDialog(false);
      fetchVenues();
    } catch (error) {
      toast.error('Failed to create venue');
    }
  };

  // Get filtered venues based on search
  const filteredVenues = venues.filter(v => 
    v.name.toLowerCase().includes(venueSearchQuery.toLowerCase())
  );

  const toggleContact = (contactId) => {
    setExpandedContacts(prev => ({
      ...prev,
      [contactId]: !prev[contactId]
    }));
  };

  const openNewEventRequestDialog = (contact = null) => {
    setVenueSearchQuery('');
    setShowVenueDropdown(false);
    if (contact) {
      setNewEventRequest({
        contact_name: contact.name,
        contact_email: contact.email,
        contact_phone: contact.phone || '',
        event_date: '',
        start_time: '',
        end_time: '',
        event_type: eventTypes[0]?.name || '',
        venue: '',
        guest_count: '',
        budget: '',
        package_id: '',
        package: '',
        selected_addons: [],
        total_amount: '',
        deposit_amount: '',
        notes: ''
      });
      setSelectedContact(contact);
    } else {
      setNewEventRequest({
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        event_date: '',
        start_time: '',
        end_time: '',
        event_type: eventTypes[0]?.name || '',
        venue: '',
        guest_count: '',
        budget: '',
        package_id: '',
        package: '',
        selected_addons: [],
        total_amount: '',
        deposit_amount: '',
        notes: ''
      });
      setSelectedContact(null);
    }
    setShowEventRequestDialog(true);
  };

  const handleCreateEventRequest = async (forceCreate = false) => {
    try {
      // Check availability unless forcing
      if (!forceCreate && newEventRequest.event_date) {
        setCheckingAvailability(true);
        const availabilityResult = await checkDateAvailability(newEventRequest.event_date);
        setCheckingAvailability(false);
        
        if (!availabilityResult.available) {
          setAvailabilityWarningData({
            date: newEventRequest.event_date,
            reason: availabilityResult.reason
          });
          setShowAvailabilityWarning(true);
          return;
        }
      }
      
      await axios.post(`${API}/event-requests`, newEventRequest);
      toast.success('Event request created successfully');
      setShowEventRequestDialog(false);
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event request');
    }
  };

  const handleCreateContact = async () => {
    try {
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, newContact);
        toast.success('Contact updated successfully');
      } else {
        await axios.post(`${API}/contacts`, newContact);
        toast.success('Contact created successfully');
      }
      setShowContactDialog(false);
      setEditingContact(null);
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact and all their event requests?')) return;
    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      toast.success('Contact deleted');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleSetPassword = async () => {
    if (!newPassword.trim()) {
      toast.error('Password is required');
      return;
    }
    try {
      await axios.post(`${API}/contacts/${passwordContact.id}/set-password`, { password: newPassword });
      toast.success('Password set successfully');
      setShowPasswordDialog(false);
      setPasswordContact(null);
      setNewPassword('');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to set password');
    }
  };

  const handleLoginAsContact = async (contact) => {
    try {
      const response = await axios.get(`${API}/contacts/${contact.id}/login-as`);
      const portalToken = response.data.portal_token;
      window.open(`/portal/${portalToken}`, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No bookings found for this contact');
    }
  };

  const handleCopyPortalLink = async (contact) => {
    try {
      const response = await axios.get(`${API}/contacts/${contact.id}/login-as`);
      const portalToken = response.data.portal_token;
      const portalUrl = `${window.location.origin}/portal/${portalToken}`;
      await navigator.clipboard.writeText(portalUrl);
      toast.success('Portal link copied to clipboard!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No bookings found for this contact');
    }
  };

  const handleAddInternalNote = async (contactId) => {
    if (!newInternalNote.trim()) return;
    try {
      await axios.post(`${API}/contacts/${contactId}/notes`, { text: newInternalNote });
      toast.success('Note added');
      setNewInternalNote('');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteInternalNote = async (contactId, noteId) => {
    try {
      await axios.delete(`${API}/contacts/${contactId}/notes/${noteId}`);
      toast.success('Note deleted');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleUpdateEventRequestStatus = async (requestId, newStatus) => {
    try {
      await axios.put(`${API}/event-requests/${requestId}`, { status: newStatus });
      toast.success('Status updated');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleMarkSeen = async (requestId) => {
    try {
      await axios.post(`${API}/event-requests/${requestId}/mark-seen`);
      fetchContacts();
    } catch (error) {
      console.error('Failed to mark as seen:', error);
    }
  };

  const handleConvertToBooking = async (requestId) => {
    try {
      const response = await axios.post(`${API}/event-requests/${requestId}/convert-to-booking`);
      toast.success('Converted to booking!');
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to booking');
    }
  };

  const handleDeleteEventRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this event request?')) return;
    try {
      await axios.delete(`${API}/event-requests/${requestId}`);
      toast.success('Event request deleted');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete event request');
    }
  };

  const openEditContact = (contact) => {
    setEditingContact(contact);
    setNewContact({
      name: contact.name,
      email: contact.email,
      phone: contact.phone || '',
      contact_type: contact.contact_type || 'contact',
      notes: contact.notes || ''
    });
    setShowContactDialog(true);
  };

  const getContactTypeBadge = (type) => {
    const typeInfo = CONTACT_TYPES.find(t => t.value === type);
    if (!typeInfo) return null;
    return (
      <Badge className={`${typeInfo.color} text-white text-xs`}>
        {typeInfo.label}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const statusInfo = LEAD_STATUSES.find(s => s.value === status);
    if (!statusInfo) return null;
    return (
      <Badge className={`${statusInfo.color} text-white text-xs`}>
        {statusInfo.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const filteredContacts = contacts.filter(contact => {
    const query = searchQuery.toLowerCase();
    return (
      contact.name.toLowerCase().includes(query) ||
      contact.email.toLowerCase().includes(query) ||
      (contact.phone && contact.phone.includes(query))
    );
  });

  // Sort contacts: those with new event requests first, then by name based on sortOrder
  const sortedContacts = [...filteredContacts].sort((a, b) => {
    // New requests always come first
    if (a.has_new && !b.has_new) return -1;
    if (!a.has_new && b.has_new) return 1;
    
    // Then sort by name
    const nameA = (a.name || '').toLowerCase();
    const nameB = (b.name || '').toLowerCase();
    if (sortOrder === 'asc') {
      return nameA.localeCompare(nameB);
    } else {
      return nameB.localeCompare(nameA);
    }
  });

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const totalNewRequests = contacts.reduce((sum, c) => 
    sum + (c.event_requests?.filter(er => er.is_new).length || 0), 0
  );

  return (
    <Layout user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo}>
      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground flex items-center gap-3">
              <User className="w-6 h-6 sm:w-8 sm:h-8 text-neon-blue" />
              Contacts
              {totalNewRequests > 0 && (
                <Badge className="bg-red-500 text-white animate-pulse">
                  {totalNewRequests} New
                </Badge>
              )}
            </h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">Manage your contacts and their event requests</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                setEditingContact(null);
                setNewContact({ name: '', email: '', phone: '', notes: '' });
                setShowContactDialog(true);
              }}
              variant="outline"
              className="border-border flex-1 sm:flex-none"
              data-testid="new-contact-btn"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">New Contact</span>
            </Button>
            <Button
              onClick={() => openNewEventRequestDialog()}
              className="bg-neon-blue hover:bg-neon-blue/80 text-black flex-1 sm:flex-none"
              data-testid="new-event-request-btn"
            >
              <CalendarPlus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">New Event Request</span>
            </Button>
          </div>
        </div>

          {/* Search and Sort */}
          <div className="flex gap-2 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search contacts by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-card border-border"
                data-testid="contact-search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={toggleSortOrder}
              className="border-border flex items-center gap-2 whitespace-nowrap"
              data-testid="sort-contacts-btn"
            >
              {sortOrder === 'asc' ? (
                <ArrowUp className="w-4 h-4" />
              ) : (
                <ArrowDown className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Name {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}</span>
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Total Contacts</p>
                    <p className="text-2xl font-bold text-foreground">{contacts.length}</p>
                  </div>
                  <User className="w-8 h-8 text-neon-blue opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">New Requests</p>
                    <p className="text-2xl font-bold text-red-400">{totalNewRequests}</p>
                  </div>
                  <Sparkles className="w-8 h-8 text-red-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Total Requests</p>
                    <p className="text-2xl font-bold text-foreground">
                      {contacts.reduce((sum, c) => sum + (c.event_requests?.length || 0), 0)}
                    </p>
                  </div>
                  <Calendar className="w-8 h-8 text-purple-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Total Bookings</p>
                    <p className="text-2xl font-bold text-green-400">
                      {contacts.reduce((sum, c) => sum + (c.bookings?.length || 0), 0)}
                    </p>
                  </div>
                  <Calendar className="w-8 h-8 text-green-400 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contacts List */}
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading contacts...</div>
          ) : sortedContacts.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="py-12 text-center">
                <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No contacts yet. Create your first contact or event request!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {sortedContacts.map((contact) => (
                <Card 
                  key={contact.id} 
                  className={`bg-card border-border transition-all ${contact.has_new ? 'border-l-4 border-l-red-500' : ''}`}
                  data-testid={`contact-card-${contact.id}`}
                >
                  <CardContent className="p-0">
                    {/* Contact Header - Clickable */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-muted/30 transition-colors flex items-center justify-between"
                      onClick={() => toggleContact(contact.id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-neon-blue/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-neon-blue" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-foreground">{contact.name}</h3>
                            {getContactTypeBadge(contact.contact_type)}
                            {contact.has_new && (
                              <Badge className="bg-red-500 text-white text-xs animate-pulse">
                                New Request
                              </Badge>
                            )}
                            {contact.password && (
                              <Key className="w-3 h-3 text-green-500" title="Has password set" />
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {contact.email}
                            </span>
                            {contact.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {contact.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm">
                          <p className="text-muted-foreground">
                            {contact.event_requests?.length || 0} requests · {contact.bookings?.length || 0} bookings
                          </p>
                        </div>
                        {expandedContacts[contact.id] ? (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {expandedContacts[contact.id] && (
                      <div className="border-t border-border p-4 bg-muted/10">
                        {/* Contact Actions */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); openEditContact(contact); }}
                            className="border-border"
                          >
                            <Edit2 className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openNewEventRequestDialog(contact); }}
                            className="bg-neon-blue/20 text-neon-blue hover:bg-neon-blue/30"
                          >
                            <CalendarPlus className="w-3 h-3 mr-1" />
                            Add Event
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setEmailRecipient({
                                email: contact.email,
                                name: contact.name,
                                contact_id: contact.id
                              });
                              setShowEmailDialog(true);
                            }}
                            className="border-neon-purple/30 text-neon-purple hover:bg-neon-purple/10"
                          >
                            <Send className="w-3 h-3 mr-1" />
                            Send Email
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setPasswordContact(contact);
                              setNewPassword('');
                              setShowPasswordDialog(true);
                            }}
                            className="border-border"
                          >
                            <Key className="w-3 h-3 mr-1" />
                            {contact.password ? 'Change Password' : 'Set Password'}
                          </Button>
                          {contact.bookings?.length > 0 && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); handleCopyPortalLink(contact); }}
                                className="border-neon-blue/30 text-neon-blue hover:bg-neon-blue/10"
                                data-testid={`copy-portal-link-${contact.id}`}
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Copy Portal Link
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); handleLoginAsContact(contact); }}
                                className="border-green-500/30 text-green-500 hover:bg-green-500/10"
                              >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                Login As
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); handleDeleteContact(contact.id); }}
                            className="text-red-500 hover:text-red-400 hover:bg-red-500/10 ml-auto"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        </div>

                        {/* Event Requests Section */}
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            Event Requests ({contact.event_requests?.length || 0})
                          </h4>
                          {contact.event_requests?.length > 0 ? (
                            <div className="space-y-2">
                              {contact.event_requests.map((er) => (
                                <div 
                                  key={er.id}
                                  className={`p-3 rounded-lg border ${er.is_new ? 'bg-red-500/10 border-red-500/30' : 'bg-card border-border'}`}
                                  onClick={() => er.is_new && handleMarkSeen(er.id)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {er.is_new && (
                                        <Sparkles className="w-4 h-4 text-red-400 animate-pulse" />
                                      )}
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium">{er.event_type}</span>
                                          {getStatusBadge(er.status)}
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                                          <span className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {formatDate(er.event_date)}
                                          </span>
                                          {er.venue && (
                                            <span>{er.venue}</span>
                                          )}
                                          {er.budget && (
                                            <span className="text-green-400">{formatCurrency(er.budget)}</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {er.status !== 'booked' && er.status !== 'lost' && (
                                        <>
                                          <Select
                                            value={er.status}
                                            onValueChange={(value) => handleUpdateEventRequestStatus(er.id, value)}
                                          >
                                            <SelectTrigger className="w-32 h-8 text-xs">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {LEAD_STATUSES.map((status) => (
                                                <SelectItem key={status.value} value={status.value}>
                                                  {status.label}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                          <Button
                                            size="sm"
                                            onClick={() => handleConvertToBooking(er.id)}
                                            className="bg-green-600 hover:bg-green-700 text-white h-8"
                                          >
                                            Convert to Booking
                                          </Button>
                                        </>
                                      )}
                                      {er.booking_id && (
                                        <>
                                          <Badge className="bg-green-500 text-white">
                                            Booked
                                          </Badge>
                                          <Button
                                            size="sm"
                                            onClick={() => window.location.href = `/bookings?open=${er.booking_id}`}
                                            className="bg-neon-purple hover:bg-neon-purple/80 text-white h-8"
                                            data-testid={`manage-event-request-btn-${er.id}`}
                                          >
                                            Manage Event
                                          </Button>
                                        </>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDeleteEventRequest(er.id)}
                                        className="text-red-500 hover:text-red-400 h-8"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground py-2">No event requests yet</p>
                          )}
                        </div>

                        {/* Bookings Section */}
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-green-400" />
                            Bookings ({contact.bookings?.length || 0})
                          </h4>
                          {contact.bookings?.length > 0 ? (
                            <div className="space-y-2">
                              {contact.bookings.map((booking) => (
                                <div 
                                  key={booking.id}
                                  className="p-3 rounded-lg bg-green-500/10 border border-green-500/30"
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">{booking.event_type}</span>
                                        <Badge className={`text-white text-xs ${
                                          booking.status === 'completed' ? 'bg-gray-500' :
                                          booking.status === 'confirmed' ? 'bg-green-500' :
                                          'bg-yellow-500'
                                        }`}>
                                          {booking.status}
                                        </Badge>
                                      </div>
                                      <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                                        <span className="flex items-center gap-1">
                                          <Calendar className="w-3 h-3" />
                                          {formatDate(booking.event_date)}
                                        </span>
                                        <span>{booking.venue}</span>
                                        <span className="text-green-400">{formatCurrency(booking.total_amount)}</span>
                                      </div>
                                    </div>
                                    <Button
                                      size="sm"
                                      onClick={() => window.location.href = `/bookings?open=${booking.id}`}
                                      className="bg-neon-purple hover:bg-neon-purple/80 text-white"
                                      data-testid={`manage-event-btn-${booking.id}`}
                                    >
                                      Manage Event
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground py-2">No bookings yet</p>
                          )}
                        </div>

                        {/* Internal Notes Section */}
                        <div>
                          <h4 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                            <StickyNote className="w-4 h-4 text-yellow-400" />
                            Internal Notes ({contact.internal_notes?.length || 0})
                          </h4>
                          <div className="bg-card rounded-lg border border-border p-3">
                            {/* Add Note Form */}
                            <div className="flex gap-2 mb-3">
                              <Input
                                placeholder="Add a private note..."
                                value={expandedContacts[contact.id] === true ? newInternalNote : ''}
                                onChange={(e) => setNewInternalNote(e.target.value)}
                                className="bg-muted border-border flex-1"
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && newInternalNote.trim()) {
                                    handleAddInternalNote(contact.id);
                                  }
                                }}
                              />
                              <Button
                                size="sm"
                                onClick={() => handleAddInternalNote(contact.id)}
                                disabled={!newInternalNote.trim()}
                                className="bg-yellow-600 hover:bg-yellow-700"
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                            {/* Notes List */}
                            {contact.internal_notes?.length > 0 ? (
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {contact.internal_notes.slice().reverse().map((note) => (
                                  <div 
                                    key={note.id}
                                    className="p-2 rounded bg-yellow-500/10 border border-yellow-500/20 text-sm"
                                  >
                                    <div className="flex justify-between items-start gap-2">
                                      <p className="text-foreground flex-1">{note.text}</p>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDeleteInternalNote(contact.id, note.id)}
                                        className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                    <p className="text-muted-foreground text-xs mt-1 flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {new Date(note.created_at).toLocaleString()}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">No internal notes yet. Notes added here are only visible to you and your team.</p>
                            )}
                          </div>
                        </div>

                        {/* Communications Section */}
                        <div className="mt-4">
                          <h4 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                            <Mail className="w-4 h-4 text-neon-purple" />
                            Communications
                          </h4>
                          <div className="bg-card rounded-lg border border-border p-3">
                            <CommunicationsLog 
                              contactId={contact.id} 
                              refreshTrigger={communicationsRefresh}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

      {/* New Event Request Dialog */}
      <Dialog open={showEventRequestDialog} onOpenChange={setShowEventRequestDialog}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">
              {selectedContact ? `New Event Request for ${selectedContact.name}` : 'New Event Request'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!selectedContact && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-foreground">Contact Name *</Label>
                    <Input
                      value={newEventRequest.contact_name}
                      onChange={(e) => setNewEventRequest({ ...newEventRequest, contact_name: e.target.value })}
                      className="bg-muted border-border"
                      placeholder="John Doe"
                      data-testid="event-contact-name"
                    />
                  </div>
                  <div>
                    <Label className="text-foreground">Email *</Label>
                    <Input
                      type="email"
                      value={newEventRequest.contact_email}
                      onChange={(e) => setNewEventRequest({ ...newEventRequest, contact_email: e.target.value })}
                      className="bg-muted border-border"
                      placeholder="john@example.com"
                      data-testid="event-contact-email"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-foreground">Phone</Label>
                  <Input
                    value={newEventRequest.contact_phone}
                    onChange={(e) => setNewEventRequest({ ...newEventRequest, contact_phone: e.target.value })}
                    className="bg-muted border-border"
                    placeholder="(555) 123-4567"
                    data-testid="event-contact-phone"
                  />
                </div>
              </>
            )}
            
            {/* Event Date and Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-foreground">Event Date *</Label>
                <Input
                  type="date"
                  value={newEventRequest.event_date}
                  onChange={(e) => setNewEventRequest({ ...newEventRequest, event_date: e.target.value })}
                  className="bg-muted border-border"
                  data-testid="event-date"
                />
              </div>
              <div>
                <Label className="text-foreground">Event Type *</Label>
                <Select
                  value={newEventRequest.event_type || "Other"}
                  onValueChange={(value) => setNewEventRequest({ ...newEventRequest, event_type: value })}
                >
                  <SelectTrigger className="bg-muted border-border" data-testid="event-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Other">Other</SelectItem>
                    {eventTypes.map((type) => (
                      <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Start/End Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-foreground">Start Time</Label>
                <Input
                  type="time"
                  value={newEventRequest.start_time}
                  onChange={(e) => setNewEventRequest({ ...newEventRequest, start_time: e.target.value })}
                  className="bg-muted border-border"
                  data-testid="event-start-time"
                />
              </div>
              <div>
                <Label className="text-foreground">End Time</Label>
                <Input
                  type="time"
                  value={newEventRequest.end_time}
                  onChange={(e) => setNewEventRequest({ ...newEventRequest, end_time: e.target.value })}
                  className="bg-muted border-border"
                  data-testid="event-end-time"
                />
              </div>
            </div>
            
            {/* Venue with Search */}
            <div className="space-y-2" ref={venueSearchRef}>
              <Label className="text-foreground">Venue</Label>
              <div className="relative">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={venueSearchQuery || newEventRequest.venue}
                    onChange={handleVenueSearchChange}
                    onFocus={() => setShowVenueDropdown(true)}
                    placeholder="Search existing venues..."
                    className="bg-muted border-border pl-9"
                    data-testid="event-venue-input"
                  />
                </div>
                
                {showVenueDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-xl shadow-xl max-h-60 overflow-y-auto">
                    {filteredVenues.length > 0 ? (
                      <>
                        {filteredVenues.map((venue, idx) => (
                          <div
                            key={venue.id || idx}
                            onClick={() => selectVenue(venue)}
                            className="px-4 py-3 hover:bg-muted cursor-pointer border-b border-border last:border-b-0 text-foreground"
                            data-testid={`venue-result-${idx}`}
                          >
                            <div className="font-medium">{venue.name}</div>
                            <div className="text-sm text-muted-foreground flex items-center gap-2">
                              {venue.venue_type}
                              {venue.city && <span>• {venue.city}</span>}
                            </div>
                          </div>
                        ))}
                        <div
                          onClick={openAddVenueDialog}
                          className="px-4 py-3 hover:bg-primary/10 cursor-pointer flex items-center gap-2 text-primary border-t border-border"
                          data-testid="add-new-venue-option"
                        >
                          <Plus className="w-4 h-4" />
                          Add "{venueSearchQuery || newEventRequest.venue}" as new venue
                        </div>
                      </>
                    ) : venueSearchQuery ? (
                      <div
                        onClick={openAddVenueDialog}
                        className="px-4 py-3 hover:bg-primary/10 cursor-pointer flex items-center gap-2 text-primary"
                        data-testid="add-new-venue-option"
                      >
                        <Plus className="w-4 h-4" />
                        No venues found - Add "{venueSearchQuery}" as new venue
                      </div>
                    ) : venues.length > 0 ? (
                      venues.slice(0, 5).map((venue, idx) => (
                        <div
                          key={venue.id || idx}
                          onClick={() => selectVenue(venue)}
                          className="px-4 py-3 hover:bg-muted cursor-pointer border-b border-border last:border-b-0 text-foreground"
                          data-testid={`venue-result-${idx}`}
                        >
                          <div className="font-medium">{venue.name}</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-2">
                            {venue.venue_type}
                            {venue.city && <span>• {venue.city}</span>}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div
                        onClick={openAddVenueDialog}
                        className="px-4 py-3 hover:bg-primary/10 cursor-pointer flex items-center gap-2 text-primary"
                        data-testid="add-new-venue-option"
                      >
                        <Plus className="w-4 h-4" />
                        Add new venue
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Guest Count */}
            <div>
              <Label className="text-foreground">Guest Count</Label>
              <Input
                type="number"
                value={newEventRequest.guest_count}
                onChange={(e) => setNewEventRequest({ ...newEventRequest, guest_count: e.target.value })}
                className="bg-muted border-border"
                placeholder="150"
                data-testid="event-guest-count"
              />
            </div>
            
            {/* Package Selection */}
            <div className="space-y-2">
              <Label className="text-foreground flex items-center gap-2">
                <Package className="w-4 h-4" />
                Package
              </Label>
              <Select
                value={newEventRequest.package_id || "none"}
                onValueChange={(value) => value !== "none" && handlePackageSelect(value)}
              >
                <SelectTrigger className="bg-muted border-border" data-testid="event-package-select">
                  <SelectValue placeholder="Select a package..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No package selected</SelectItem>
                  {packages.map((pkg) => (
                    <SelectItem key={pkg.id} value={pkg.id}>
                      {pkg.name} - ${pkg.base_price?.toLocaleString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Add-ons (show only if package selected, filtered by package) */}
            {newEventRequest.package_id && addons.length > 0 && (
              <div className="space-y-2">
                <Label className="text-foreground">Add-Ons</Label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 bg-muted/50 rounded-lg">
                  {addons
                    .filter(addon => {
                      // Filter add-ons by package availability
                      const availableForPackages = addon.available_for_packages || [];
                      // If no packages specified, available for all
                      if (availableForPackages.length === 0) return true;
                      // Otherwise, check if current package is in the list
                      return availableForPackages.includes(newEventRequest.package_id);
                    })
                    .map((addon) => (
                    <div
                      key={addon.id}
                      className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${
                        newEventRequest.selected_addons.includes(addon.id)
                          ? 'bg-primary/20 border-primary'
                          : 'border-border hover:bg-muted'
                      }`}
                      onClick={() => handleAddonToggle(addon.id)}
                      data-testid={`addon-${addon.id}`}
                    >
                      <Checkbox
                        checked={newEventRequest.selected_addons.includes(addon.id)}
                        onCheckedChange={() => handleAddonToggle(addon.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{addon.name}</p>
                        <p className="text-xs text-muted-foreground">${addon.price?.toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Total Amount and Deposit */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-foreground">Total Amount</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={newEventRequest.total_amount}
                    onChange={(e) => setNewEventRequest({ ...newEventRequest, total_amount: e.target.value })}
                    className="bg-muted border-border pl-9"
                    placeholder="0.00"
                    data-testid="event-total-amount"
                  />
                </div>
              </div>
              <div>
                <Label className="text-foreground">Deposit Amount</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={newEventRequest.deposit_amount}
                    onChange={(e) => setNewEventRequest({ ...newEventRequest, deposit_amount: e.target.value })}
                    className="bg-muted border-border pl-9"
                    placeholder="0.00"
                    data-testid="event-deposit-amount"
                  />
                </div>
              </div>
            </div>
            
            {/* Notes */}
            <div>
              <Label className="text-foreground">Notes</Label>
              <Textarea
                value={newEventRequest.notes}
                onChange={(e) => setNewEventRequest({ ...newEventRequest, notes: e.target.value })}
                className="bg-muted border-border"
                placeholder="Any additional details..."
                rows={3}
                data-testid="event-notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventRequestDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleCreateEventRequest()}
              disabled={checkingAvailability}
              className="bg-neon-blue hover:bg-neon-blue/80 text-black"
              data-testid="create-event-btn"
            >
              {checkingAvailability ? 'Checking...' : 'Create Event Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Venue Dialog */}
      <Dialog open={showAddVenueDialog} onOpenChange={setShowAddVenueDialog}>
        <DialogContent className="bg-card border border-border max-w-md" data-testid="add-venue-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2 text-foreground">
              <MapPin className="w-5 h-5 text-primary" />
              Add New Venue
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-foreground">Venue Name *</Label>
              <Input
                value={newVenueData.name}
                onChange={(e) => setNewVenueData({...newVenueData, name: e.target.value})}
                placeholder="Venue name"
                className="bg-background border-border text-foreground"
                data-testid="new-venue-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Address</Label>
              <Input
                value={newVenueData.address}
                onChange={(e) => setNewVenueData({...newVenueData, address: e.target.value})}
                placeholder="123 Main Street"
                className="bg-background border-border text-foreground"
                data-testid="new-venue-address-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">City</Label>
              <Input
                value={newVenueData.city}
                onChange={(e) => setNewVenueData({...newVenueData, city: e.target.value})}
                placeholder="City"
                className="bg-background border-border text-foreground"
                data-testid="new-venue-city-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-foreground">Venue Type</Label>
              <Select value={newVenueData.venue_type} onValueChange={(v) => setNewVenueData({...newVenueData, venue_type: v})}>
                <SelectTrigger className="bg-background border-border text-foreground" data-testid="new-venue-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="Ballroom">Ballroom</SelectItem>
                  <SelectItem value="Outdoor">Outdoor</SelectItem>
                  <SelectItem value="Restaurant">Restaurant</SelectItem>
                  <SelectItem value="Country Club">Country Club</SelectItem>
                  <SelectItem value="Banquet Hall">Banquet Hall</SelectItem>
                  <SelectItem value="Hotel">Hotel</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddVenueDialog(false)} className="border-border">
                Cancel
              </Button>
              <Button onClick={saveNewVenue} className="bg-primary text-primary-foreground hover:bg-primary/80">
                <Save className="w-4 h-4 mr-2" />
                Add Venue & Use
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* New/Edit Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">
              {editingContact ? 'Edit Contact' : 'New Contact'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-foreground">Name *</Label>
              <Input
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                className="bg-muted border-border"
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label className="text-foreground">Email *</Label>
              <Input
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                className="bg-muted border-border"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label className="text-foreground">Phone</Label>
              <Input
                value={newContact.phone}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                className="bg-muted border-border"
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <Label className="text-foreground">Contact Type</Label>
              <Select
                value={newContact.contact_type || "contact"}
                onValueChange={(value) => setNewContact({ ...newContact, contact_type: value })}
              >
                <SelectTrigger className="bg-muted border-border">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-foreground">Notes</Label>
              <Textarea
                value={newContact.notes}
                onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                className="bg-muted border-border"
                placeholder="Any notes about this contact..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowContactDialog(false); setEditingContact(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateContact}
              className="bg-neon-blue hover:bg-neon-blue/80 text-black"
            >
              {editingContact ? 'Update Contact' : 'Create Contact'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Date Availability Warning Dialog */}
      <Dialog open={showAvailabilityWarning} onOpenChange={setShowAvailabilityWarning}>
        <DialogContent className="bg-card border border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Date Not Available
            </DialogTitle>
            <DialogDescription className="text-muted-foreground pt-2">
              {availabilityWarningData && (
                <>
                  <p className="mb-2">
                    The date <span className="font-semibold text-foreground">{availabilityWarningData.date}</span> is not available.
                  </p>
                  {availabilityWarningData.reason && (
                    <p className="text-yellow-400 text-sm mb-3">{availabilityWarningData.reason}</p>
                  )}
                  <p>Would you like to continue anyway?</p>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowAvailabilityWarning(false);
                setAvailabilityWarningData(null);
              }}
              className="border-border"
            >
              Choose Different Date
            </Button>
            <Button
              onClick={() => {
                setShowAvailabilityWarning(false);
                setAvailabilityWarningData(null);
                handleCreateEventRequest(true);
              }}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              Continue Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground flex items-center gap-2">
              <Key className="w-5 h-5 text-neon-blue" />
              {passwordContact?.password ? 'Change Password' : 'Set Password'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {passwordContact && (
                <>
                  Set a password for <span className="font-semibold text-foreground">{passwordContact.name}</span> to allow them to access the client portal.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-foreground">New Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-muted border-border pr-10"
                  placeholder="Enter password..."
                  data-testid="contact-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                The contact will use their email and this password to log into the client portal.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { 
                setShowPasswordDialog(false); 
                setPasswordContact(null);
                setNewPassword('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSetPassword}
              disabled={!newPassword.trim()}
              className="bg-neon-blue hover:bg-neon-blue/80 text-black"
              data-testid="save-password-btn"
            >
              Save Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Compose Dialog */}
      <EmailComposeDialog
        open={showEmailDialog}
        onOpenChange={setShowEmailDialog}
        recipient={emailRecipient}
        variables={{
          client_name: emailRecipient?.name || '',
          client_email: emailRecipient?.email || '',
          company_name: user?.contact_name || ''
        }}
        onEmailSent={() => setCommunicationsRefresh(prev => prev + 1)}
      />
    </Layout>
  );
}
